import { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useApp } from "../../context/AuthContext";

export default function StoreProductPurchasePanel({ product }) {
  const { addToCart, setCartOpen } = useApp();

  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const hasVariants = !!product?.has_variants && variants.length > 0;

  const groupedVariants = useMemo(() => {
    return variants.reduce((acc, variant) => {
      const key = variant?.variant_type || "Variante";
      if (!acc[key]) acc[key] = [];
      acc[key].push(variant);
      return acc;
    }, {});
  }, [variants]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    hasVariants ? variants[0]?.id || null : null
  );
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (hasVariants) {
      setSelectedVariantId(variants[0]?.id || null);
    } else {
      setSelectedVariantId(null);
    }
    setQuantity(1);
  }, [hasVariants, variants, product?.id]);

  const selectedVariant = useMemo(() => {
    if (!hasVariants) return null;
    return (
      variants.find((item) => String(item.id) === String(selectedVariantId)) ||
      variants[0] ||
      null
    );
  }, [hasVariants, selectedVariantId, variants]);

  const hasOffer =
    !hasVariants &&
    product?.has_offer &&
    product?.final_offer_price != null &&
    Number(product.final_offer_price) < Number(product.final_price || 0);

  const unitPrice = hasVariants
    ? Number(selectedVariant?.final_price ?? 0)
    : Number(hasOffer ? product?.final_offer_price : product?.final_price ?? 0);

  const unitOriginalPrice = hasVariants ? null : Number(product?.final_price ?? 0);
  const totalPrice = unitPrice * quantity;

  const displayedCode = useMemo(() => {
    const baseCode = product?.product_code || product?.code || "-";
    const variantSku = selectedVariant?.sku || "";
    if (variantSku) return `${baseCode} - ${variantSku}`;
    return baseCode;
  }, [product, selectedVariant]);

  const variantLabel = useMemo(() => {
    if (!selectedVariant) return "";
    const type = selectedVariant?.variant_type || "";
    const value = selectedVariant?.variant_value || "";
    if (type && value) return `${type}: ${value}`;
    return value || type || "";
  }, [selectedVariant]);

  const handleDecrease = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  const handleIncrease = () => {
    setQuantity((prev) => prev + 1);
  };

  const handleAddToCart = () => {
    const cartItem = {
      key: `${product?.id}-${selectedVariant?.id || "base"}`,
      productId: product?.id,
      variantId: selectedVariant?.id || null,
      slug: product?.slug || "",
      name: product?.name || "",
      image: selectedVariant?.image || product?.image || "",
      quantity,
      price: Number(unitPrice || 0),
      originalPrice: hasOffer ? Number(product?.final_price ?? 0) : null,
      code: product?.product_code || product?.code || "",
      sku: selectedVariant?.sku || "",
      variantLabel,
      variantType: selectedVariant?.variant_type || "",
      variantValue: selectedVariant?.variant_value || "",
    };

    addToCart(cartItem);
    setCartOpen(true);
  };

  const handleShareWhatsApp = () => {
    const url = window.location.href;
    const variantText = selectedVariant
      ? `\nVariante: ${selectedVariant.variant_type}: ${selectedVariant.variant_value}`
      : "";
    const message = `Hola, podría interesarte este producto de NovaXfit:\n${product?.name || ""}\nCódigo: ${displayedCode}${variantText}\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "620px",
        mx: "auto",
        px: { xs: 0, md: 0.4 },
        py: 0,
      }}
    >
      {product?.brand && (
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 500,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: 0.35,
            mb: 0.8,
          }}
        >
          {product.brand}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: { xs: 24, md: 30 },
          fontWeight: 700,
          color: "#111827",
          lineHeight: 1.06,
          mb: 1.1,
        }}
      >
        {product?.name}
      </Typography>

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: hasOffer ? 0.35 : 0.65 }}
      >
        <Typography
          sx={{
            fontSize: { xs: 28, md: 34 },
            fontWeight: 700,
            color: hasOffer ? "#e50914" : "#111827",
            lineHeight: 1,
          }}
        >
          ${unitPrice.toFixed(2)}
        </Typography>

        {hasOffer && (
          <Typography
            sx={{
              fontSize: { xs: 10, md: 13 },
              fontWeight: 700,
              color: "#9ca3af",
              textDecoration: "line-through",
              mb: 0.4,
            }}
          >
            ${unitOriginalPrice.toFixed(2)}
          </Typography>
        )}
      </Stack>

      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: "#6b7280",
          mb: 1.5,
        }}
      >
        Precio incluye IVA
      </Typography>

      <Typography
        sx={{
          fontSize: 13.2,
          fontWeight: 500,
          color: "#7b8190",
          mb: 1.9,
        }}
      >
        Código: {displayedCode}
      </Typography>

      {Object.entries(groupedVariants).map(([type, items]) => (
        <Box key={type} sx={{ mb: 1.9 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#111827",
              mb: 0.8,
            }}
          >
            {type}
          </Typography>

          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            {items.map((variant) => {
              const active = String(selectedVariant?.id || "") === String(variant.id);

              return (
                <Button
                  key={variant.id}
                  onClick={() => setSelectedVariantId(variant.id)}
                  sx={{
                    minWidth: 68,
                    px: 1.2,
                    py: 0.72,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 700,
                    color: active ? "#ffffff" : "#111827",
                    bgcolor: active ? "#111827" : "#ffffff",
                    border: active ? "1px solid #111827" : "1px solid #d1d5db",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: active ? "#0b1220" : "#f9fafb",
                      borderColor: "#111827",
                      boxShadow: "none",
                    },
                  }}
                >
                  {variant.variant_value}
                </Button>
              );
            })}
          </Stack>
        </Box>
      ))}

      <div className="row g-3 align-items-end">
        <div className="col-12 col-lg-4">
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#111827",
              mb: 0.8,
            }}
          >
            Cantidad
          </Typography>

          <Box
            sx={{
              height: 40,
              borderRadius: "16px",
              border: "1px solid #d1d5db",
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: "50px 1fr 50px",
              backgroundColor: "#ffffff",
              maxWidth: 150,
              mb: 2,
            }}
          >
            <IconButton
              onClick={handleDecrease}
              sx={{
                borderRadius: 0,
                color: "#4b5563",
                height: "100%",
              }}
            >
              <RemoveRoundedIcon />
            </IconButton>

            <Box
              sx={{
                borderLeft: "1px solid #e5e7eb",
                borderRight: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                {quantity}
              </Typography>
            </Box>

            <IconButton
              onClick={handleIncrease}
              sx={{
                borderRadius: 0,
                color: "#4b5563",
                height: "100%",
              }}
            >
              <AddRoundedIcon />
            </IconButton>
          </Box>
        </div>

        <div className="col-12 col-lg-8">
          <Box
            sx={{
              borderRadius: "18px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e5e7eb",
              px: 1.5,
              py: 1.3,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#4b5563",
                mb: 0.15,
              }}
            >
              Precio total
            </Typography>

            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "#111827",
                lineHeight: 1,
              }}
            >
              ${totalPrice.toFixed(2)}
            </Typography>
          </Box>
        </div>
      </div>

      <Stack direction="row" spacing={1.2} sx={{ mt: 2.1, flexWrap: "wrap" }}>
        <Button
          onClick={handleAddToCart}
          startIcon={<ShoppingCartOutlinedIcon />}
          sx={{
            minWidth: 0,
            width: { xs: "100%", sm: "auto" },
            px: { xs: 1.8, sm: 2.4 },
            height: 52,
            borderRadius: "16px",
            textTransform: "none",
            fontSize: 15,
            fontWeight: 800,
            color: "#111827",
            bgcolor: "#fbbf24",
            border: "1px solid #fbbf24",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#f59e0b",
              borderColor: "#f59e0b",
              boxShadow: "none",
            },
          }}
        >
          Añadir al carrito
        </Button>

        <Button
          onClick={handleShareWhatsApp}
          startIcon={<WhatsAppIcon />}
          sx={{
            minWidth: 0,
            width: { xs: "100%", sm: "auto" },
            px: { xs: 1.8, sm: 2.2 },
            height: 52,
            borderRadius: "16px",
            textTransform: "none",
            fontSize: 15,
            fontWeight: 700,
            color: "#111827",
            bgcolor: "#ffffff",
            border: "1px solid #d1d5db",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#f9fafb",
              borderColor: "#9ca3af",
              boxShadow: "none",
            },
          }}
        >
          Compartir
        </Button>
      </Stack>
    </Box>
  );
}